import WGLLog from './logger';

const TEMPSERIAL4 = new Float32Array(16);
const TEMPSERIAL3 = new Float32Array(9);

const _logInfo = (key, msg) => {
  WGLLog.info('Shader', key, msg);
};

const _logError = (key, msg) => {
  WGLLog.error('Shader', key, msg);
};

const checkCompileStatus = (gl, key, id) => {
  if (gl.getShaderParameter(id, gl.COMPILE_STATUS)) {
    _logInfo(key, 'Compile status is OK');
  } else {
    _logError(key, 'Compile failed! Info log:');
    WGLLog.errorRaw(gl.getShaderInfoLog(id));
  }
};

const checkLinkStatus = (gl, key, id) => {
  if (gl.getProgramParameter(id, gl.LINK_STATUS)) {
    _logInfo(key, 'Link status is OK');
  } else {
    _logError(key, 'Link failed! Info log:');
    WGLLog.errorRaw(gl.getProgramInfoLog(id));
  }
};

class WGLShader {
  // opts: { srcVert, srcFrag, uNames, vaNames }
  constructor(gl, key, opts) {
    // vertex shader
    const idVert = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(idVert, opts.srcVert);
    gl.compileShader(idVert);
    checkCompileStatus(gl, `${key} (vert)`, idVert);

    // fragment shader
    const idFrag = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(idFrag, opts.srcFrag);
    gl.compileShader(idFrag);
    checkCompileStatus(gl, `${key} (frag)`, idFrag);

    // vertex attributes
    const vertexAttribs = []; // array of { name, index }
    for (let i = 0; i < opts.vaNames.length; i++) {
      const vaName = opts.vaNames[i];
      vertexAttribs.push({ name: vaName, index: i });
    }

    // shader program
    const idProg = gl.createProgram();
    gl.attachShader(idProg, idVert);
    gl.attachShader(idProg, idFrag);

    for (let i = 0; i < vertexAttribs.length; i++) {
      const va = vertexAttribs[i];
      gl.bindAttribLocation(idProg, va.index, va.name);
    }

    gl.linkProgram(idProg);
    checkLinkStatus(gl, `${key} (prog)`, idProg);

    // detach and delete shaders
    gl.detachShader(idProg, idVert);
    gl.detachShader(idProg, idFrag);

    gl.deleteShader(idVert);
    gl.deleteShader(idFrag);

    // uniform locations
    const uniformLocations = {}; // map name -> location
    for (let i = 0; i < opts.uNames.length; i++) {
      const uName = opts.uNames[i];
      uniformLocations[uName] = gl.getUniformLocation(idProg, uName);
    }

    // save properties
    this.key = key;
    this.idProg = idProg;
    this.uniformLocations = uniformLocations;
    this.vertexAttribs = vertexAttribs;

    this._log('Created');
  }

  _log(msg) {
    _logInfo(this.key, msg);
  }

  use(gl) {
    const { idProg, vertexAttribs } = this;

    gl.useProgram(idProg);
    for (let i = 0; i < vertexAttribs.length; i++) {
      const va = vertexAttribs[i];
      gl.enableVertexAttribArray(va.index);
    }
  }

  prepareVertexAttribs(gl, drawable) {
    const { vertexAttribs } = this;

    for (let i = 0; i < vertexAttribs.length; i++) {
      const va = vertexAttribs[i];
      drawable.prepareVertexAttrib(gl, va.name, va.index);
    }
  }

  setU1i(gl, uName, x) {
    gl.uniform1i(this.uniformLocations[uName], x);
  }

  setU2i(gl, uName, x, y) {
    gl.uniform2i(this.uniformLocations[uName], x, y);
  }

  setU1f(gl, uName, x) {
    gl.uniform1f(this.uniformLocations[uName], x);
  }

  setU2f(gl, uName, x, y) {
    gl.uniform2f(this.uniformLocations[uName], x, y);
  }

  setUVec3(gl, uName, u) {
    gl.uniform3f(this.uniformLocations[uName], u[0], u[1], u[2]);
  }

  setUVec4(gl, uName, u) {
    gl.uniform4f(this.uniformLocations[uName], u[0], u[1], u[2], u[3]);
  }

  // matrices: column-major 1D arrays
  setUMat3Raw(gl, uName, s) {
    gl.uniformMatrix3fv(this.uniformLocations[uName], false, s);
  }

  setUMat4Raw(gl, uName, s) {
    gl.uniformMatrix4fv(this.uniformLocations[uName], false, s);
  }

  unuse(gl) {
    const { vertexAttribs } = this;

    for (let i = 0; i < vertexAttribs.length; i++) {
      const va = vertexAttribs[i];
      gl.disableVertexAttribArray(va.index);
    }
    gl.useProgram(null);
  }

  free(gl) {
    const { idProg } = this;

    gl.deleteProgram(idProg);

    this._log('Deleted');
  }
}

export default WGLShader;
